<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Sales Office</h2>
        <nav v-if="user.level >= 2" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>

    </div>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Address</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">
            <p v-if="community.meta?.sales_office?.address?.street1">{{ community.meta?.sales_office?.address?.street1 }}</p>
            <p v-if="community.meta?.sales_office?.address?.street2">{{ community.meta?.sales_office?.address?.street2 }}</p>
            <p v-if="community.meta?.sales_office?.address?.city && community.meta?.sales_office?.address?.state && community.meta?.sales_office?.address?.zip">{{ community.meta?.sales_office?.address?.city }} {{ community.meta?.sales_office?.address?.state }} {{ community.meta?.sales_office?.address?.zip }}</p>
        </dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Geolocation</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
            <p>Latitude: {{ community.meta?.sales_office?.geo?.lat }}º</p>
            <p>Longitude: {{ community.meta?.sales_office?.geo?.lng }}º</p>
        </dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Located Inside Community</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.meta?.sales_office?.address?.in_community ? "Yes" : "No" }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Leads Email</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.meta?.sales_office?.email }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Phone</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.meta?.sales_office?.phone }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50 lg:row-span-7">Hours</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal" v-for="day,i in community.meta?.sales_office?.hours" :class="i == 0 ? 'border-t-[2px] border-bhav-cool-gray-50' : ''" :key="`hours-${i}`">
            {{ (new Intl.DateTimeFormat('en-us', { weekday:'long' })).format(new Date(0, 0, (i + 1))) }}:
            <template v-if="day?.appointment || false">
                By Appointment
            </template>
            <template v-else>
                {{ day.start ? new Intl.DateTimeFormat('en-us', { hour:'numeric', minute:"2-digit", hour12: true })
                .formatRange(
                    new Date(2024, 0, (i + 1), ...time(day.start)),
                    new Date(2024, 0, (i + 1), ...time(day.end))
                ) :
                'Closed'
            }}
            </template>
        </dd>

    </dl>

    <form @submit="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-else-if="user.level >= 2">
        <legend class="sr-only">Sales Office Settings</legend>

        <h3 class="font-normal text-bhav-blue-300 py-5">Office Address</h3>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-3 gap-2">
            <p class="lg:col-span-3 flex items-center gap-2">
                <ToggleButton id="sales_office.address.in_community" @changed="(e) => { sales_office.address.in_community = !sales_office.address.in_community }" :checked="sales_office.address.in_community"
                      :label="sales_office.address.in_community ? 'In the Community' : 'Outside the Community'"/>
                <label for="sales_office.address.in_community" class="text-sm leading-5 font-medium text-bhav-blue-300">Address is Inside the Community</label>
            </p>
            <p class="lg:col-span-3 grid gap-2">
                <label for="sales_office.address.street1" class="text-sm leading-5 font-medium text-bhav-blue-300">Street Address 1</label>
                <TextInput id="sales_office.address.street1" v-model="sales_office.address.street1"/>
            </p>
            <p class="lg:col-span-3 grid gap-2">
                <label for="sales_office.address.street2" class="text-sm leading-5 font-medium text-bhav-blue-300">Street Address 2</label>
                <TextInput id="sales_office.address.street2" v-model="sales_office.address.street2"/>
            </p>
            <p class="grid gap-2">
                <label for="sales_office.address.city" class="text-sm leading-5 font-medium text-bhav-blue-300">City</label>
                <TextInput id="sales_office.address.city" v-model="sales_office.address.city"/>
            </p>
            <p class="grid gap-2">
                <label for="sales_office.address.state" class="text-sm leading-5 font-medium text-bhav-blue-300">State</label>
                <SelectField id="sales_office.address.state" class="w-full" v-model="sales_office.address.state" empty="Select a State..." :options="states" />
            </p>
            <p class="grid gap-2">
                <label for="sales_office.address.zip" class="text-sm leading-5 font-medium text-bhav-blue-300">Zip Code</label>
                <NumberField id="sales_office.address.zip" width="w-full" :min="0" :max="99999" :arrows="false" v-model="sales_office.address.zip" />
            </p>
        </div>

        <p class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Geolocation</p>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-6 gap-2 border-t-[2px] border-bhav-cool-gray-50">
            <p class="grid gap-2">
                <label for="sales_office.geo.lat" class="text-sm leading-5 font-medium text-bhav-blue-300">Latitude</label>
                <CoordinateField suffix="º" id="sales_office.geo.lat" width="w-full" :max="90" :min="-90" v-model="sales_office.geo.lat" :class="{'ring-2 ring-bhav-red-500': showGeoErrors && !isGeoValueSet(sales_office.geo.lat)}" />
            </p>
            <p class="grid gap-2">
                <label for="sales_office.geo.lng" class="text-sm leading-5 font-medium text-bhav-blue-300">Longitude</label>
                <CoordinateField suffix="º" id="sales_office.geo.lng" width="w-full" :max="180" :min="-180" v-model="sales_office.geo.lng" :class="{'ring-2 ring-bhav-red-500': showGeoErrors && !isGeoValueSet(sales_office.geo.lng)}" />
            </p>
        </div>

        <label for="sales_office.email" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Leads Email</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <input type="email" id="sales_office.email" class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 max-w-80" v-model="sales_office.email" />
        </div>

        <label for="sales_office.phone" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Phone Number</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <PhoneNumber id="sales_office.phone" v-model="sales_office.phone" />
        </div>

        <label for="sales_office.hours" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50 lg:row-span-7">Hours</label>
        <div v-for="day,i in sales_office.hours" :key="`hours-${i}`" :class="i == 0 ? 'border-t-[2px] border-bhav-cool-gray-50' : ''" class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-6 items-center gap-4">
            <span class="text-bhav-blue-500 ">{{ new Intl.DateTimeFormat('en-us', { weekday: 'long' }).format( new Date(2024, 0, (i + 1))) }}</span>
            <span class="inline-flex text-bhav-blue-500 items-center gap-2">
                <ToggleButton :id="`open-${i}`" :checked="day.start !== null" @changed="(e) => {
                    day.start = day.start === null ? '09:00' : null;
                    day.end = day.end === null ? '17:00' : null;
                    day.appointment = day.start === null ? false : (day?.appointment || false);
                }"
                :label="day.start ? 'Open' : 'Closed'" />
                <label :for="`open-${i}`" class="text-bhav-blue-500 ">{{ day.start !== null ? "Open" : "Closed" }}</label>
            </span>
            <span v-if="day.start !== null" :class="(day?.appointment || false) && 'opacity-50'" class="lg:col-span-2 text-bhav-blue-500 inline-flex items-center gap-2">
                <label :for="`start-${i}`" class="sr-only">From</label>
                <input :disabled="(day?.appointment || false)" :class="(day?.appointment || false) && 'cursor-not-allowed'" :id="`start-${i}`" type="time" v-model="day.start" class="text-bhav-blue-500 bg-bhav-cool-gray-50 w-full p-2 rounded [&::-webkit-calendar-picker-indicator]:hidden" />
                <span aria-hidden="true">-</span>
                <label :for="`end-${i}`" class="sr-only">To</label>
                <input :disabled="(day?.appointment || false)" :class="(day?.appointment || false) && 'cursor-not-allowed'" :id="`end-${i}`" type="time" v-model="day.end" class="text-bhav-blue-500 bg-bhav-cool-gray-50 w-full p-2 rounded [&::-webkit-calendar-picker-indicator]:hidden" />
            </span>
            <span v-if="day.start !== null" class="inline-flex  text-bhav-blue-500 items-center gap-2 lg:col-span-2">
                <ToggleButton :id="`appt-${i}`" :checked="day?.appointment || false" @changed="(val) => {
                    day.appointment = val;
                }" :label="day?.appointment || false ? 'By Appointment' : 'Walk-Ins'" />
                <label :for="`appt-${i}`" class="text-bhav-blue-500">By Appointment</label>
            </span>
        </div>
    </form>
       
</template>

<script>
import { formatToCurrency } from '@/helpers/numberHelpers';
import {FormButton, NumberField, CoordinateField, SelectField, ToggleButton, PhoneNumber, TextInput} from '@/components/form';
import {states} from '@/helpers/globals';
import community from '@/models/legacy/community';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';

export default {
    data() {
        return {
            sales_office: {
                ...community.meta.sales_office
            },
            states,
            editing: false,
            showGeoErrors: false
        }
    },
    methods: {
        time(value) {
            value = value !== null ? value : '';
            let time = value.match(/([0-9]{0,2}):?([0-9]{0,2})/);
            time[1] = time[1] != '' ? time[1] : 0;
            time[2] = time[2] != '' ? time[2] : 0;
            return [
                time[1], 
                time[2]
            ];
        },
        edit() {
            if(this.user.level < 2) return;
            this.editing = true;
            this.showGeoErrors = false;
            let sales_office = JSON.parse(JSON.stringify(this.community.meta.sales_office));
            this.sales_office = {
                ...this.sales_office,
                ...sales_office
            }
        },
        cancel() {
            this.editing = false;
            this.showGeoErrors = false;
        },
        isGeoValueSet(value) {
            return value !== null && value !== undefined && value !== '';
        },
        save() {
            if(this.user.level < 2) return;
            if(!this.isGeoValueSet(this.sales_office?.geo?.lat) || !this.isGeoValueSet(this.sales_office?.geo?.lng)) {
                this.showGeoErrors = true;
                this.setAlert({
                    text: 'Latitude and Longitude are required.',
                    level: 'error'
                });
                return;
            }
            let community = this.community;
            community.meta.sales_office = {
                ...community.sales_office,
                ...this.sales_office
            }
            this.$emit('update', community);
            this.cancel();
        },
        formatToCurrency,
        ...mapActions(useMessageStore, ['setAlert'])
    },
    computed: {
        ...mapState(useAuthStore, ['user'])
    },
    emits: [
        'update'
    ],
    props: {
        community: {
            type: Object,
            required: true
        }
    },
    components: {
        FormButton,
        NumberField,
        CoordinateField,
        SelectField,
        ToggleButton,
        PhoneNumber,
        TextInput
    }
}
</script>